// Central de notificações (sino no cabeçalho) e preferências de aviso.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClock, AtSign, Bell, CheckCheck, Lock, MessageSquareReply, Newspaper, UserPlus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { NotificationDTO, NotificationType } from '@jynexus/shared';
import { api } from '../lib/api.ts';
import { timeAgo } from '../lib/format.ts';
import { clsx } from './ui.tsx';

const ICON: Record<NotificationType, typeof Bell> = { assigned: UserPlus, mentioned: AtSign, replied: MessageSquareReply, blocked: Lock, overdue: AlarmClock, digest: Newspaper };
export function NotificationBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const q = useQuery({ queryKey: ['notifications'], queryFn: () => api.notifications(), refetchInterval: 60_000, staleTime: 20_000 });
  const read = useMutation({ mutationFn: (b: { ids?: string[]; all?: boolean }) => api.readNotifications(b), onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }) });
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const unread = q.data?.unread ?? 0;
  const items = q.data?.items ?? [];
  const Item = ({ n }: { n: NotificationDTO }) => {
    const Icon = ICON[n.type];
    const body = (
      <>
        <Icon className={clsx('mt-0.5 size-4 shrink-0', n.read ? 'text-slate-300' : 'text-accent-600')} />
        <span className="min-w-0 flex-1">
          <span className={clsx('block text-sm leading-snug', n.read ? 'text-slate-500' : 'font-medium text-slate-800')}>{n.title}</span>
          {n.body && n.type !== 'digest' && <span className="mt-0.5 line-clamp-2 block text-xs text-slate-500">{n.body}</span>}
          <span className="text-[11px] text-slate-400">{timeAgo(n.createdAt)}</span>
        </span>
      </>
    );
    const cls = 'flex gap-2 px-3 py-2 text-left hover:bg-slate-50';
    const onClick = () => (!n.read && read.mutate({ ids: [n.id] }), setOpen(false));
    return n.taskKey ? <Link to={`/tasks/${n.taskKey}`} onClick={onClick} className={cls}>{body}</Link> : <button onClick={onClick} className={clsx(cls, 'w-full')}>{body}</button>;
  };
  return (
    <div ref={box} className="relative">
      <button onClick={() => setOpen((o) => !o)} className="relative inline-flex size-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800" aria-label={unread ? `Notificações (${unread} não lidas)` : 'Notificações'} aria-expanded={open}>
        <Bell className="size-5" />
        {unread > 0 && <span className="absolute top-1 right-1 min-w-4 rounded-full bg-accent-500 px-1 text-center text-[10px] leading-4 font-semibold text-white">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-1 w-96 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
            <span className="text-sm font-semibold text-slate-800">Notificações</span>
            <span className="flex items-center gap-3 text-xs">
              {unread > 0 && <button onClick={() => read.mutate({ all: true })} className="inline-flex items-center gap-1 text-navy-700 hover:underline"><CheckCheck className="size-3.5" /> Marcar todas como lidas</button>}
              <Link to="/settings/notifications" onClick={() => setOpen(false)} className="text-slate-500 hover:underline">Configurar</Link>
            </span>
          </div>
          <div className="max-h-[60vh] divide-y divide-slate-50 overflow-y-auto">
            {items.length ? items.map((n) => <Item key={n.id} n={n} />) : <p className="p-4 text-center text-sm text-slate-500">Nada por aqui.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
